import { useState, useEffect } from "react";
import {
  Save,
  Database,
  RefreshCw,
  Building2,
  CreditCard,
  Bell,
  BarChart3,
  Shield,
  Server,
  Eye,
  EyeOff,
  Share2,
  Loader2,
  Bot,
  CheckCircle2,
  XCircle,
} from "lucide-react";

const API_BASE = import.meta.env.VITE_API_URL || "https://ckgo2-api.martin-94d.workers.dev";

interface Settings {
  company: {
    companyName: string;
    ico: string;
    dic: string;
    licenseNumber: string;
    address: string;
    email: string;
    phone: string;
    bankAccount: string;
    iban: string;
    swift: string;
    bankName: string;
  };
  smtp: {
    host: string;
    port: number;
    username: string;
    password: string;
    encryption: string;
    fromName: string;
    fromEmail: string;
    replyTo: string;
  };
  notifications: {
    newOrder: boolean;
    orderPaid: boolean;
    orderCancelled: boolean;
    newInquiry: boolean;
    newReview: boolean;
    lowCapacity: boolean;
    recipients: string[];
    slackWebhook: string;
  };
  booking: {
    depositPercent: number;
    depositDueDays: number;
    finalPaymentDaysBefore: number;
    minParticipants: number;
    maxParticipants: number;
    cancellationFees: { daysFrom: number; daysTo: number; feePercent: number }[];
  };
  seo: {
    siteTitle: string;
    metaDescription: string;
    keywords: string;
    googleAnalyticsId: string;
    googleTagManagerId: string;
    facebookPixelId: string;
    hotjarId: string;
    // GA4 Data API & Google Ads API
    ga4PropertyId: string;
    googleAdsCustomerId: string;
    googleAdsDeveloperToken: string;
    googleServiceAccountJson: string;
  };
  social: {
    facebook: string;
    instagram: string;
    youtube: string;
    tiktok: string;
    linkedin: string;
    twitter: string;
  };
  security: {
    sessionTimeoutMinutes: number;
    require2fa: boolean;
    logAccess: boolean;
    emailOnNewLogin: boolean;
    minPasswordLength: number;
    passwordExpirationDays: number;
  };
  ai: {
    openaiApiKey: string;
    openaiModel: string;
    enableSegmentation: boolean;
    enablePersonalization: boolean;
  };
}

type SettingsCategory = keyof Settings;

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState<SettingsCategory | "cache">("company");
  const [settings, setSettings] = useState<Settings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [isTestingAi, setIsTestingAi] = useState(false);
  const [aiTestResult, setAiTestResult] = useState<"success" | "error" | null>(null);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch settings on mount
  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/settings`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(data.data);
      } else {
        setMessage({ type: "error", text: "Nepodařilo se načíst nastavení" });
      }
    } catch {
      setMessage({ type: "error", text: "Chyba připojení k serveru" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!settings || activeTab === "cache") return;

    setIsSaving(true);
    setMessage(null);

    try {
      const response = await fetch(`${API_BASE}/api/admin/settings/${activeTab}`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(settings[activeTab]),
      });

      if (response.ok) {
        const data = await response.json();
        setSettings((prev) => (prev ? { ...prev, [activeTab]: data.data } : prev));
        setMessage({ type: "success", text: "Nastavení bylo uloženo" });
        setHasChanges(false);
      } else {
        setMessage({ type: "error", text: "Nepodařilo se uložit nastavení" });
      }
    } catch {
      setMessage({ type: "error", text: "Chyba připojení k serveru" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClearCache = async () => {
    setIsClearing(true);
    setMessage(null);

    try {
      const response = await fetch(`${API_BASE}/api/admin/cache/clear`, {
        method: "POST",
        credentials: "include",
      });

      if (response.ok) {
        setMessage({ type: "success", text: "Cache byla úspěšně vymazána" });
      } else {
        setMessage({ type: "error", text: "Nepodařilo se vymazat cache" });
      }
    } catch {
      setMessage({ type: "error", text: "Chyba připojení k serveru" });
    } finally {
      setIsClearing(false);
    }
  };

  const handleTestSmtp = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/settings/smtp/test`, {
        method: "POST",
        credentials: "include",
      });

      const data = await response.json();
      setMessage({ type: response.ok ? "success" : "error", text: data.message });
    } catch {
      setMessage({ type: "error", text: "Chyba připojení k serveru" });
    }
  };

  const handleTestAi = async () => {
    setIsTestingAi(true);
    setAiTestResult(null);
    try {
      const response = await fetch(`${API_BASE}/api/admin/settings/ai/test`, {
        method: "POST",
        credentials: "include",
      });

      const data = await response.json();
      if (response.ok) {
        setAiTestResult("success");
        setMessage({ type: "success", text: data.message || "Připojení k OpenAI funguje" });
      } else {
        setAiTestResult("error");
        setMessage({ type: "error", text: data.message || "Nepodařilo se připojit k OpenAI" });
      }
    } catch {
      setAiTestResult("error");
      setMessage({ type: "error", text: "Chyba připojení k serveru" });
    } finally {
      setIsTestingAi(false);
    }
  };

  const updateSetting = <T extends SettingsCategory>(
    category: T,
    key: keyof Settings[T],
    value: any
  ) => {
    setSettings((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [category]: { ...prev[category], [key]: value },
      };
    });
    setHasChanges(true);
  };

  const tabs = [
    { id: "company" as const, name: "Firma", icon: Building2 },
    { id: "smtp" as const, name: "SMTP", icon: Server },
    { id: "notifications" as const, name: "Notifikace", icon: Bell },
    { id: "booking" as const, name: "Rezervace", icon: CreditCard },
    { id: "seo" as const, name: "SEO & Analytics", icon: BarChart3 },
    { id: "social" as const, name: "Soc. sítě", icon: Share2 },
    { id: "security" as const, name: "Zabezpečení", icon: Shield },
    { id: "ai" as const, name: "AI (ChatGPT)", icon: Bot },
    { id: "cache" as const, name: "Cache", icon: Database },
  ];

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="rounded-lg bg-red-50 p-4 text-red-700">
        Nepodařilo se načíst nastavení. Zkuste obnovit stránku.
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Nastavení</h1>
        <p className="mt-1 text-gray-500 dark:text-gray-400">
          Správa nastavení webu a systému
        </p>
      </div>

      {message && (
        <div
          className={`mb-6 rounded-lg p-4 ${
            message.type === "success"
              ? "bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400"
              : "bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400"
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="rounded-xl bg-white shadow-sm dark:bg-gray-800">
        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex gap-1 overflow-x-auto px-4 sm:gap-2 sm:px-6" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  if (hasChanges && activeTab !== "cache") {
                    if (confirm("Máte neuložené změny. Chcete pokračovat bez uložení?")) {
                      setActiveTab(tab.id);
                      setHasChanges(false);
                    }
                  } else {
                    setActiveTab(tab.id);
                  }
                }}
                className={`flex shrink-0 items-center gap-2 border-b-2 px-3 py-4 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? "border-primary-500 text-primary-600 dark:text-primary-400"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                }`}
              >
                <tab.icon className="h-4 w-4" />
                <span className="hidden sm:inline">{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab content */}
        <div className="p-6">
          {/* Company Info */}
          {activeTab === "company" && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Firemní údaje</h3>

              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Název firmy
                  </label>
                  <input
                    type="text"
                    value={settings.company.companyName}
                    onChange={(e) => updateSetting("company", "companyName", e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    IČO
                  </label>
                  <input
                    type="text"
                    value={settings.company.ico}
                    onChange={(e) => updateSetting("company", "ico", e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    DIČ
                  </label>
                  <input
                    type="text"
                    value={settings.company.dic}
                    onChange={(e) => updateSetting("company", "dic", e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Číslo koncese CK
                  </label>
                  <input
                    type="text"
                    value={settings.company.licenseNumber}
                    onChange={(e) => updateSetting("company", "licenseNumber", e.target.value)}
                    placeholder="Např. 123456/2020"
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Adresa sídla
                </label>
                <textarea
                  rows={2}
                  value={settings.company.address}
                  onChange={(e) => updateSetting("company", "address", e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Kontaktní email
                  </label>
                  <input
                    type="email"
                    value={settings.company.email}
                    onChange={(e) => updateSetting("company", "email", e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Telefon
                  </label>
                  <input
                    type="tel"
                    value={settings.company.phone}
                    onChange={(e) => updateSetting("company", "phone", e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>

              <h3 className="mt-8 text-lg font-medium text-gray-900 dark:text-white">Bankovní spojení</h3>

              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Číslo účtu
                  </label>
                  <input
                    type="text"
                    value={settings.company.bankAccount}
                    onChange={(e) => updateSetting("company", "bankAccount", e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    IBAN
                  </label>
                  <input
                    type="text"
                    value={settings.company.iban}
                    onChange={(e) => updateSetting("company", "iban", e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    SWIFT/BIC
                  </label>
                  <input
                    type="text"
                    value={settings.company.swift}
                    onChange={(e) => updateSetting("company", "swift", e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Název banky
                  </label>
                  <input
                    type="text"
                    value={settings.company.bankName}
                    onChange={(e) => updateSetting("company", "bankName", e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
            </div>
          )}

          {/* SMTP Settings */}
          {activeTab === "smtp" && (
            <div className="space-y-6">
              <div className="rounded-lg bg-blue-50 p-4 text-sm text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                <strong>Tip:</strong> Pro produkční použití doporučujeme služby jako Resend, SendGrid, Mailgun nebo Amazon SES.
              </div>

              <h3 className="text-lg font-medium text-gray-900 dark:text-white">SMTP Server</h3>

              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    SMTP Host
                  </label>
                  <input
                    type="text"
                    value={settings.smtp.host}
                    onChange={(e) => updateSetting("smtp", "host", e.target.value)}
                    placeholder="smtp.example.com"
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Port
                  </label>
                  <select
                    value={settings.smtp.port}
                    onChange={(e) => updateSetting("smtp", "port", parseInt(e.target.value))}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  >
                    <option value={587}>587 (TLS - doporučeno)</option>
                    <option value={465}>465 (SSL)</option>
                    <option value={25}>25 (nešifrováno)</option>
                    <option value={2525}>2525 (alternativní)</option>
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Uživatelské jméno
                  </label>
                  <input
                    type="text"
                    value={settings.smtp.username}
                    onChange={(e) => updateSetting("smtp", "username", e.target.value)}
                    placeholder="user@example.com"
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Heslo
                  </label>
                  <div className="relative">
                    <input
                      type={showSmtpPassword ? "text" : "password"}
                      value={settings.smtp.password}
                      onChange={(e) => updateSetting("smtp", "password", e.target.value)}
                      placeholder="••••••••"
                      className="w-full rounded-lg border border-gray-300 px-4 py-2.5 pr-10 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showSmtpPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Šifrování
                </label>
                <div className="flex gap-4">
                  {["tls", "ssl", "none"].map((enc) => (
                    <label key={enc} className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="encryption"
                        value={enc}
                        checked={settings.smtp.encryption === enc}
                        onChange={(e) => updateSetting("smtp", "encryption", e.target.value)}
                        className="text-primary-600"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {enc === "tls" ? "TLS" : enc === "ssl" ? "SSL" : "Žádné"}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <h3 className="mt-8 text-lg font-medium text-gray-900 dark:text-white">Odesílatel</h3>

              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Jméno odesílatele
                  </label>
                  <input
                    type="text"
                    value={settings.smtp.fromName}
                    onChange={(e) => updateSetting("smtp", "fromName", e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Email odesílatele (From)
                  </label>
                  <input
                    type="email"
                    value={settings.smtp.fromEmail}
                    onChange={(e) => updateSetting("smtp", "fromEmail", e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Reply-To email
                  </label>
                  <input
                    type="email"
                    value={settings.smtp.replyTo}
                    onChange={(e) => updateSetting("smtp", "replyTo", e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>

              <div className="mt-6">
                <button
                  type="button"
                  onClick={handleTestSmtp}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Odeslat testovací email
                </button>
              </div>
            </div>
          )}

          {/* Notifications */}
          {activeTab === "notifications" && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Emailové notifikace</h3>

              <div className="space-y-4">
                {[
                  { key: "newOrder" as const, label: "Nová objednávka", desc: "Při vytvoření nové objednávky" },
                  { key: "orderPaid" as const, label: "Platba přijata", desc: "Když zákazník zaplatí" },
                  { key: "orderCancelled" as const, label: "Storno objednávky", desc: "Při zrušení objednávky" },
                  { key: "newInquiry" as const, label: "Nová poptávka", desc: "Při příchodu nezávazné poptávky" },
                  { key: "newReview" as const, label: "Nová recenze", desc: "Když zákazník napíše recenzi" },
                  { key: "lowCapacity" as const, label: "Nízká kapacita", desc: "Když zbývají méně než 3 místa" },
                ].map((item) => (
                  <label key={item.key} className="flex items-start gap-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                    <input
                      type="checkbox"
                      checked={settings.notifications[item.key]}
                      onChange={(e) => updateSetting("notifications", item.key, e.target.checked)}
                      className="mt-1 h-4 w-4 rounded text-primary-600"
                    />
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{item.label}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{item.desc}</p>
                    </div>
                  </label>
                ))}
              </div>

              <h3 className="mt-8 text-lg font-medium text-gray-900 dark:text-white">Příjemci notifikací</h3>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Emaily (oddělené čárkou)
                </label>
                <input
                  type="text"
                  value={settings.notifications.recipients.join(", ")}
                  onChange={(e) => updateSetting("notifications", "recipients", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <h3 className="mt-8 text-lg font-medium text-gray-900 dark:text-white">Slack integrace</h3>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Webhook URL
                </label>
                <input
                  type="url"
                  value={settings.notifications.slackWebhook}
                  onChange={(e) => updateSetting("notifications", "slackWebhook", e.target.value)}
                  placeholder="https://hooks.slack.com/services/..."
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
          )}

          {/* Booking Settings */}
          {activeTab === "booking" && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Nastavení rezervací</h3>

              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Výše zálohy (%)
                  </label>
                  <input
                    type="number"
                    value={settings.booking.depositPercent}
                    onChange={(e) => updateSetting("booking", "depositPercent", parseInt(e.target.value))}
                    min="0"
                    max="100"
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Splatnost zálohy (dní)
                  </label>
                  <input
                    type="number"
                    value={settings.booking.depositDueDays}
                    onChange={(e) => updateSetting("booking", "depositDueDays", parseInt(e.target.value))}
                    min="1"
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Doplatek (dní před odjezdem)
                  </label>
                  <input
                    type="number"
                    value={settings.booking.finalPaymentDaysBefore}
                    onChange={(e) => updateSetting("booking", "finalPaymentDaysBefore", parseInt(e.target.value))}
                    min="1"
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Min. počet účastníků
                  </label>
                  <input
                    type="number"
                    value={settings.booking.minParticipants}
                    onChange={(e) => updateSetting("booking", "minParticipants", parseInt(e.target.value))}
                    min="1"
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Max. počet účastníků
                  </label>
                  <input
                    type="number"
                    value={settings.booking.maxParticipants}
                    onChange={(e) => updateSetting("booking", "maxParticipants", parseInt(e.target.value))}
                    min="1"
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>

              <h3 className="mt-8 text-lg font-medium text-gray-900 dark:text-white">Storno podmínky</h3>

              <div className="space-y-4">
                {settings.booking.cancellationFees.map((fee, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <div className="w-32">
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {fee.daysFrom === 60 ? "60+" : `${fee.daysFrom}-${fee.daysTo}`} dní
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={fee.feePercent}
                        onChange={(e) => {
                          const newFees = [...settings.booking.cancellationFees];
                          newFees[index] = { ...fee, feePercent: parseInt(e.target.value) };
                          updateSetting("booking", "cancellationFees", newFees);
                        }}
                        min="0"
                        max="100"
                        className="w-20 rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      />
                      <span className="text-sm text-gray-500">% z ceny</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* SEO & Analytics */}
          {activeTab === "seo" && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Meta tagy (výchozí)</h3>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Název webu (title)
                </label>
                <input
                  type="text"
                  value={settings.seo.siteTitle}
                  onChange={(e) => updateSetting("seo", "siteTitle", e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Popis (meta description)
                </label>
                <textarea
                  rows={3}
                  value={settings.seo.metaDescription}
                  onChange={(e) => updateSetting("seo", "metaDescription", e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Klíčová slova
                </label>
                <input
                  type="text"
                  value={settings.seo.keywords}
                  onChange={(e) => updateSetting("seo", "keywords", e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <h3 className="mt-8 text-lg font-medium text-gray-900 dark:text-white">Analytics & Tracking</h3>

              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Google Analytics ID
                  </label>
                  <input
                    type="text"
                    value={settings.seo.googleAnalyticsId}
                    onChange={(e) => updateSetting("seo", "googleAnalyticsId", e.target.value)}
                    placeholder="G-XXXXXXXXXX"
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Google Tag Manager ID
                  </label>
                  <input
                    type="text"
                    value={settings.seo.googleTagManagerId}
                    onChange={(e) => updateSetting("seo", "googleTagManagerId", e.target.value)}
                    placeholder="GTM-XXXXXXX"
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Facebook Pixel ID
                  </label>
                  <input
                    type="text"
                    value={settings.seo.facebookPixelId}
                    onChange={(e) => updateSetting("seo", "facebookPixelId", e.target.value)}
                    placeholder="123456789012345"
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Hotjar ID
                  </label>
                  <input
                    type="text"
                    value={settings.seo.hotjarId}
                    onChange={(e) => updateSetting("seo", "hotjarId", e.target.value)}
                    placeholder="1234567"
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>

              <h3 className="mt-8 text-lg font-medium text-gray-900 dark:text-white">Google API (pro Analytics Dashboard)</h3>

              <div className="rounded-lg bg-blue-50 p-4 text-sm text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                <strong>Info:</strong> Tyto údaje slouží k načítání dat z Google Analytics 4 a Google Ads přímo do admin dashboardu.
                Vyžadují Service Account s přístupem k GA4 property a Google Ads účtu.
              </div>

              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    GA4 Property ID
                  </label>
                  <input
                    type="text"
                    value={settings.seo.ga4PropertyId || ""}
                    onChange={(e) => updateSetting("seo", "ga4PropertyId", e.target.value)}
                    placeholder="123456789"
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Najdete v GA4 &gt; Admin &gt; Property Settings
                  </p>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Google Ads Customer ID
                  </label>
                  <input
                    type="text"
                    value={settings.seo.googleAdsCustomerId || ""}
                    onChange={(e) => updateSetting("seo", "googleAdsCustomerId", e.target.value)}
                    placeholder="123-456-7890"
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Najdete v Google Ads &gt; nahoře v menu (formát: XXX-XXX-XXXX)
                  </p>
                </div>

                <div className="sm:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Google Ads Developer Token
                  </label>
                  <input
                    type="text"
                    value={settings.seo.googleAdsDeveloperToken || ""}
                    onChange={(e) => updateSetting("seo", "googleAdsDeveloperToken", e.target.value)}
                    placeholder="XXXXXXXXXXXXXXXXXXXXXXXX"
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 font-mono text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Získáte v Google Ads &gt; Tools &gt; API Center (vyžaduje schválení)
                  </p>
                </div>

                <div className="sm:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Google Service Account JSON
                  </label>
                  <textarea
                    rows={6}
                    value={settings.seo.googleServiceAccountJson || ""}
                    onChange={(e) => updateSetting("seo", "googleServiceAccountJson", e.target.value)}
                    placeholder='{"type": "service_account", "project_id": "...", ...}'
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 font-mono text-xs text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    JSON klíč ze Service Account v Google Cloud Console. Service Account musí mít přístup k GA4 property
                    (Admin &gt; Property Access Management) a Google Ads účtu (Admin &gt; Linked accounts).
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Social Media */}
          {activeTab === "social" && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Sociální sítě</h3>

              <div className="space-y-4">
                {[
                  { key: "facebook" as const, name: "Facebook", placeholder: "https://facebook.com/ckgo2" },
                  { key: "instagram" as const, name: "Instagram", placeholder: "https://instagram.com/ckgo2" },
                  { key: "youtube" as const, name: "YouTube", placeholder: "https://youtube.com/@ckgo2" },
                  { key: "tiktok" as const, name: "TikTok", placeholder: "https://tiktok.com/@ckgo2" },
                  { key: "linkedin" as const, name: "LinkedIn", placeholder: "https://linkedin.com/company/ckgo2" },
                  { key: "twitter" as const, name: "X (Twitter)", placeholder: "https://x.com/ckgo2" },
                ].map((social) => (
                  <div key={social.key}>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {social.name}
                    </label>
                    <input
                      type="url"
                      value={settings.social[social.key]}
                      onChange={(e) => updateSetting("social", social.key, e.target.value)}
                      placeholder={social.placeholder}
                      className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Security */}
          {activeTab === "security" && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Zabezpečení</h3>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Automatické odhlášení po (minuty)
                </label>
                <select
                  value={settings.security.sessionTimeoutMinutes}
                  onChange={(e) => updateSetting("security", "sessionTimeoutMinutes", parseInt(e.target.value))}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  <option value={30}>30 minut</option>
                  <option value={60}>1 hodina</option>
                  <option value={120}>2 hodiny</option>
                  <option value={480}>8 hodin</option>
                  <option value={1440}>24 hodin</option>
                </select>
              </div>

              <div className="space-y-4">
                {[
                  { key: "require2fa" as const, label: "Dvoufaktorové ověření (2FA)", desc: "Vyžadovat 2FA pro všechny administrátory" },
                  { key: "logAccess" as const, label: "Logování přístupů", desc: "Zaznamenávat všechny přihlášení a akce administrátorů" },
                  { key: "emailOnNewLogin" as const, label: "Email při novém přihlášení", desc: "Odeslat email při přihlášení z nového zařízení" },
                ].map((item) => (
                  <label key={item.key} className="flex items-start gap-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                    <input
                      type="checkbox"
                      checked={settings.security[item.key]}
                      onChange={(e) => updateSetting("security", item.key, e.target.checked)}
                      className="mt-1 h-4 w-4 rounded text-primary-600"
                    />
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{item.label}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{item.desc}</p>
                    </div>
                  </label>
                ))}
              </div>

              <h3 className="mt-8 text-lg font-medium text-gray-900 dark:text-white">Politika hesel</h3>

              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Minimální délka hesla
                  </label>
                  <input
                    type="number"
                    value={settings.security.minPasswordLength}
                    onChange={(e) => updateSetting("security", "minPasswordLength", parseInt(e.target.value))}
                    min="6"
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Expirace hesla (dní, 0 = nikdy)
                  </label>
                  <input
                    type="number"
                    value={settings.security.passwordExpirationDays}
                    onChange={(e) => updateSetting("security", "passwordExpirationDays", parseInt(e.target.value))}
                    min="0"
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
            </div>
          )}

          {/* AI Settings */}
          {activeTab === "ai" && (
            <div className="space-y-6">
              <div className="rounded-lg bg-gradient-to-r from-purple-50 to-blue-50 p-4 dark:from-purple-900/20 dark:to-blue-900/20">
                <div className="flex items-start gap-3">
                  <Bot className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                  <div>
                    <h3 className="font-medium text-purple-900 dark:text-purple-100">
                      OpenAI / ChatGPT integrace
                    </h3>
                    <p className="mt-1 text-sm text-purple-700 dark:text-purple-300">
                      AI se používá pro automatickou segmentaci zákazníků pomocí přirozeného jazyka
                      a personalizaci emailových kampaní. Vyžaduje platný OpenAI API klíč.
                    </p>
                  </div>
                </div>
              </div>

              <h3 className="text-lg font-medium text-gray-900 dark:text-white">API Klíč</h3>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  OpenAI API Key
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input
                      type={showApiKey ? "text" : "password"}
                      value={settings.ai?.openaiApiKey || ""}
                      onChange={(e) => updateSetting("ai", "openaiApiKey", e.target.value)}
                      placeholder="sk-..."
                      className="w-full rounded-lg border border-gray-300 px-4 py-2.5 pr-10 font-mono text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiKey(!showApiKey)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={handleTestAi}
                    disabled={isTestingAi || !settings.ai?.openaiApiKey}
                    className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    {isTestingAi ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : aiTestResult === "success" ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                    ) : aiTestResult === "error" ? (
                      <XCircle className="h-4 w-4 text-red-500" />
                    ) : (
                      <Bot className="h-4 w-4" />
                    )}
                    Test
                  </button>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  API klíč získáte na{" "}
                  <a
                    href="https://platform.openai.com/api-keys"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:underline"
                  >
                    platform.openai.com/api-keys
                  </a>
                </p>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Model
                </label>
                <select
                  value={settings.ai?.openaiModel || "gpt-4o-mini"}
                  onChange={(e) => updateSetting("ai", "openaiModel", e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  <option value="gpt-4o-mini">GPT-4o Mini (doporučeno, levnější)</option>
                  <option value="gpt-4o">GPT-4o (nejlepší kvalita)</option>
                  <option value="gpt-4-turbo">GPT-4 Turbo</option>
                  <option value="gpt-3.5-turbo">GPT-3.5 Turbo (nejlevnější)</option>
                </select>
              </div>

              <h3 className="mt-8 text-lg font-medium text-gray-900 dark:text-white">Funkce</h3>

              <div className="space-y-4">
                <label className="flex items-start gap-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                  <input
                    type="checkbox"
                    checked={settings.ai?.enableSegmentation ?? true}
                    onChange={(e) => updateSetting("ai", "enableSegmentation", e.target.checked)}
                    className="mt-1 h-4 w-4 rounded text-primary-600"
                  />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">AI Segmentace zákazníků</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Umožňuje vytvářet segmenty zákazníků pomocí přirozeného jazyka
                      (např. "zákazníci kteří byli na Bali loni")
                    </p>
                  </div>
                </label>

                <label className="flex items-start gap-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                  <input
                    type="checkbox"
                    checked={settings.ai?.enablePersonalization ?? true}
                    onChange={(e) => updateSetting("ai", "enablePersonalization", e.target.checked)}
                    className="mt-1 h-4 w-4 rounded text-primary-600"
                  />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">AI Personalizace emailů</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Automaticky upravuje obsah emailů podle profilu zákazníka
                      v automatických kampaních
                    </p>
                  </div>
                </label>
              </div>

              <div className="mt-6 rounded-lg bg-amber-50 p-4 dark:bg-amber-900/20">
                <h4 className="font-medium text-amber-900 dark:text-amber-100">Náklady na API</h4>
                <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
                  GPT-4o Mini stojí přibližně $0.15 / 1M vstupních tokenů. Průměrná segmentace
                  spotřebuje ~500 tokenů (~$0.000075). Pro běžné použití očekávejte náklady
                  jednotky dolarů měsíčně.
                </p>
              </div>
            </div>
          )}

          {/* Cache */}
          {activeTab === "cache" && (
            <div className="space-y-6">
              <div className="rounded-lg bg-blue-50 p-4 dark:bg-blue-900/30">
                <h3 className="font-medium text-blue-900 dark:text-blue-300">Informace o cache</h3>
                <p className="mt-1 text-sm text-blue-700 dark:text-blue-400">
                  Web používá 5minutovou cache pro rychlejší načítání. Změny v administraci se projeví
                  automaticky do 5 minut, nebo můžete cache vymazat ručně.
                </p>
              </div>

              <div className="flex items-center justify-between rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">Vymazat cache</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Vymaže všechna cachovaná data a vynutí obnovení z databáze
                  </p>
                </div>
                <button
                  onClick={handleClearCache}
                  disabled={isClearing}
                  className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50"
                >
                  <RefreshCw className={`h-4 w-4 ${isClearing ? "animate-spin" : ""}`} />
                  {isClearing ? "Mazání..." : "Vymazat cache"}
                </button>
              </div>

              <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                <h4 className="font-medium text-gray-900 dark:text-white">Statistiky</h4>
                <dl className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
                  <div>
                    <dt className="text-sm text-gray-500 dark:text-gray-400">Cache TTL</dt>
                    <dd className="text-lg font-semibold text-gray-900 dark:text-white">5 min</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500 dark:text-gray-400">CDN</dt>
                    <dd className="text-lg font-semibold text-gray-900 dark:text-white">Cloudflare</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500 dark:text-gray-400">Region</dt>
                    <dd className="text-lg font-semibold text-gray-900 dark:text-white">EU</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500 dark:text-gray-400">Status</dt>
                    <dd className="text-lg font-semibold text-green-600 dark:text-green-400">Aktivní</dd>
                  </div>
                </dl>
              </div>
            </div>
          )}
        </div>

        {/* Save button */}
        {activeTab !== "cache" && (
          <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4 dark:border-gray-700">
            {hasChanges && (
              <span className="text-sm text-amber-600 dark:text-amber-400">
                Máte neuložené změny
              </span>
            )}
            <div className="ml-auto">
              <button
                onClick={handleSave}
                disabled={isSaving || !hasChanges}
                className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
              >
                {isSaving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {isSaving ? "Ukládám..." : "Uložit změny"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
